import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faThermometerQuarter,
  faKeyboard,
  faSnowflake,
  faHdd,
  faTint,
  faCaretRight,
  faCaretLeft,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons'
import SensorGrid from './SensorGrid'
import ReportChart from './ReportChart'
import ReportTable from './ReportTable'
import RemoteControl from './Remote'

const icons = {
  TEM: <FontAwesomeIcon icon={faThermometerQuarter} />,
  HUM: <FontAwesomeIcon icon={faTint} />,
  IO: <FontAwesomeIcon icon={faKeyboard} />,
  FAN: <FontAwesomeIcon icon={faSnowflake} />,
  PID: <FontAwesomeIcon icon={faHdd} />
}

const typeText = {
  TEM: "Temperature",
  HUM: "Humidity",
  IO: "Input/Output",
  FAN: "Fan",
  PID: "PID"
}

const typeUnits = {
  TEM: "°C",
  HUM: "%",
  IO: "%",
  FAN: "%",
  PID: "%"
}

const ComponentInfo = ({ component, user }) => {
  const [ip, setIp] = useState(component.ip)
  const [duration, setDuration] = useState(component.duration / 60)
  const [min, setMin] = useState(component.min_value)
  const [max, setMax] = useState(component.max_value)
  const isSuperuser = user && user.is_superuser

  useEffect(() => {
    setIp(component.ip)
  }, [component.ip])

  useEffect(() => {
    setDuration(component.duration / 60)
  }, [component.duration])

  useEffect(() => {
    setMin(component.min_value)
  }, [component.min_value])

  useEffect(() => {
    setMax(component.max_value)
  }, [component.max_value])

  const setVisible = async () => {
    const params = {organisation: component.organisation}
    const data = {visible: !component.visible}
    await axios({url: `/api/v1/component/${component.name}/configure`, method: "PUT", params, data})
  }

  const submitData = async () => {
    const params = {organisation: component.organisation}
    if (duration * 60 !== component.duration || min !== component.min_value || max !== component.max_value) {
      const data = {duration: duration * 60, min_value: min, max_value: max}
      await axios({url: `/api/v1/component/${component.name}`, method: "PUT", params, data})
    }
    if (ip !== component.ip) {
      const data = {ip}
      await axios({url: `/api/v1/component/${component.name}/configure`, method: "PUT", params, data})
    }
  }

  return (
    <div className="mt-2 mx-3 w-100">
      <ul className="list-unstyled list-group text-sm">
        {isSuperuser ? <>
        <li className="list-group-item">Type: {typeText[component.type]}</li>
        <li className="list-group-item">Manufacturer: {component.manufacturer}</li>
        <li className="list-group-item p-0">
          <div className="input-group input-group-alternative">
            <span className="input-group-text">IP: </span>
            <input
             type="text"
             minLength="7"
             maxLength="15"
             size="15"
             pattern="^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$"
             className="form-control"
             value={ip}
             onChange={e => setIp(e.target.value)}
            />
          </div>
        </li>
        <li className="list-group-item p-0">
          <div className="input-group input-group-alternative">
            <span className="input-group-text">Duration: </span>
            <input
             type="number"
             className="form-control"
             min="0"
             value={duration}
             onChange={e => setDuration(e.target.value)}
            />
            <span className="input-group-text input-group-append">min</span>
          </div>
        </li>
        <li className="list-group-item p-0">
          <div className="input-group input-group-alternative">
            <span className="input-group-text">Setpoint: </span>
            <input
             type="number"
             className="form-control"
             step="0.5"
             value={min}
             onChange={e => setMin(e.target.value)}
            />
            <span className="input-group-text input-group-append">{typeUnits[component.type]}</span>
          </div>
        </li>
        <li className="list-group-item p-0 mb-6">
          <div className="input-group input-group-alternative">
            <span className="input-group-text">Limit: </span>
            <input
             type="number"
             className="form-control"
             step="0.5"
             value={max}
             onChange={e => setMax(e.target.value)}
            />
            <span className="input-group-text input-group-append">{typeUnits[component.type]}</span>
          </div>
        </li>
        <div style={{position: "absolute", bottom: "0px", height: "50px", width: "100%"}}>
          <button
           type="button"
           className={"btn btn-primary " + (component.visible ? "active" : "")}
           onClick={setVisible}
          >
          {component.visible ?
            <>Visible <FontAwesomeIcon icon={faEye}/></> :
            <>Invisible <FontAwesomeIcon icon={faEyeSlash}/></>}
          </button>
          <button
           type="button"
           className="btn btn-primary"
           onClick={submitData}
          >
            Submit
          </button>
        </div>
        </> : <>
        <li className="list-group-item">Duration: {duration} min</li>
        <li className="list-group-item">Setpoint: {min} {typeUnits[component.type]}</li>
        <li className="list-group-item">Limit: {max} {typeUnits[component.type]}</li>
        </>}
      </ul>
    </div>
  )
}

const ComponentPanel = ({ facility, component, channels, reportToggle, user }) => {
  const [report, setReport] = useState(null)
  const [leftToggle, setLeftToggle] = useState(true)
  const [rightToggle, setRightToggle] = useState(false)
  const seconds = Math.floor(facility.update_time)

  useEffect(() => {
    if (!reportToggle) {
      const params = { facility: facility.name, order_by: "-start_time", limit: 1 }
      const fetchReports = async () => {
        try {
          const rsp = await axios({url: "/api/v1/report/", method: "GET", params})
          if (rsp.data.result.length > 0) {
            setReport(rsp.data.result[0])
            setRightToggle(true)
          }
        }
        catch (e) {
          console.log(e)
        }
      }
      fetchReports()
    }
  }, [facility.name, seconds, reportToggle])

  return (
    <div className="row h-100">
      {leftToggle &&
      <div className={"pt-2 px-lg-2 px-md-0 " + (rightToggle ? "col-lg-6" : "col")}>
        <div className="card h-100">
          <div className="w-100">
          {reportToggle ?
            <ReportTable facility={facility.name} selectedReport={report} selectReport={setReport} user={user}/> :
            <SensorGrid tag={facility?.current_batch || report?.name} component={component} channels={channels} user={user}/>
          }
          </div>
          <div
           className=""
           style={{position: "absolute", right: "8px"}}
           onClick={() => setRightToggle(!rightToggle)}
          >
            {rightToggle ? <FontAwesomeIcon icon={faCaretRight}/> : <FontAwesomeIcon icon={faCaretLeft}/>}
          </div>
        </div>
      </div>}
      {rightToggle &&
      <div className={"pt-2 px-lg-2 px-md-0 " + (leftToggle ? "col-lg-6" : "col")}>
        <div className="card h-100">
          <div
           style={{position: "absolute", left: "8px", cursor: "pointer"}}
           onClick={() => setLeftToggle(!leftToggle)}
          >
          {leftToggle ? <FontAwesomeIcon icon={faCaretLeft}/> : <FontAwesomeIcon icon={faCaretRight}/>}
          </div>
          {report &&
          <div className="w-100">
            <ReportChart
              user={user}
              facility={facility}
              component={component}
              report={report}
              onClick={() => setLeftToggle(!leftToggle)}
            />
          </div>
          }
        </div>
      </div>}
    </div>
  )
}

export default function Dashboard({ facility, user }) {
  const [activeComponent, setComponentActive] = useState(0)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [reportToggle, setReportToggle] = useState(false)
  const isSuperuser = user && user.is_superuser
  let items = Object.values(facility.components).sort((a, b) => (+a.visible) > (+b.visible))
  if (!isSuperuser) items = items.filter(i=> i.visible)
  const component = items[activeComponent]
  const channels = component ? Object.values(facility.channels).filter(c => c.component === component.name) : []
  const key = "component.selected"

  useEffect(() => {
    const openTab = localStorage.getItem(key)
    if (openTab === null) {
      setComponentActive(0)
    }
    else {
      setComponentActive(openTab)
    }
  }, [items.length])

  useEffect(() => {
    if (items.length > 0 && activeComponent >= items.length) {
      setComponentActive(0)
    }
  }, [activeComponent, items.length])

  useEffect(() => {
    localStorage.setItem(key, activeComponent)
  }, [activeComponent])

  return (
    <div className='container-fluid'>
      <div className="row">
        <div className="col">
          <div className="row min-vh-100">
            <div className="col-lg-2 px-0 pt-2">
              <div className="card shadow pt-1 px-1 h-100">
                <RemoteControl facility={facility} user={user}/>
                <button
                 key="report toggle"
                 className={"btn btn-primary mb-2 w-100 text-center " + (reportToggle ? "active" : "")}
                 onClick={() => setReportToggle(!reportToggle)}
                >
                  <span>{reportToggle ? "Show grid" : "Show reports"}</span>
                </button>
                <hr className="mt-1 mb-2"/>
                <div className="dropdown w-100">
                  <button
                   className="w-100 btn btn-light dropdown-toggle "
                   onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    {component && icons[component.type]}
                    <span className="px-1 text-uppercase">{component && component.name.replaceAll("_", " ")}</span>
                  </button>
                  <div className={"dropdown-menu " + (dropdownOpen ? "show" : "")} style={{left: "-2px", right: "-2px"}}>
                    <div className="dropdown-header">
                      <h6>Select component:</h6>
                    </div>
                    {(items.length > 1) && items.map((component, index) => (
                      <div
                        key={component.id}
                        className={"dropdown-item text-center " + (activeComponent === index ? "font-weight-bold active" : "")}
                        onClick={() => {
                         setComponentActive(index)
                         setDropdownOpen(false)
                        }}
                      >
                        <div className="icon icon-shape">
                          {icons[component.type]}
                          <span className="px-2 text-uppercase">
                            {component.name.replaceAll("_", " ")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="row">
                  {component && <ComponentInfo component={component} user={user}/>}
                </div>
              </div>
            </div>
            <div className="col-lg-10">
              {component && <ComponentPanel
               facility={facility}
               component={component}
               channels={channels}
               reportToggle={reportToggle}
               user={user}
              />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
